// Restore pipeline – obnova navigačního stavu z URL search params
// Parse URL descriptorů -> decode proti registru -> ready/lazy/error vrstva

import type { AgendaRegistry } from './agendaRegistry';
import type { NavigationState } from './navigationState';
import { recomputeNavigationState } from './navigationState';
import type { LayerDescriptor, NavigationRestoreError } from './layerDescriptor';
import { generateLayerId, createErrorLayerDescriptor } from './layerDescriptor';
import { parseStack } from './navigationSerializer';
import type { UrlLayerDescriptor } from './navigationSerializer';
import type { ParamCodecMap } from './paramCodec';

/**
 * Obnoví navigační stav z URL search params.
 * Dvoufázový proces:
 * 1. Parse URL -> surové layer descriptory
 * 2. Decode proti registru -> ready/lazy/error vrstvy
 *
 * Nevalidní vrstvy vytvoří lokální error layer, zbytek stacku zůstane zachován.
 * Po reloadu se okamžitě aktivuje jen vrchní vrstva; podkladové jsou lazy.
 */
export function restoreFromUrl(
  searchParams: URLSearchParams,
  registry: AgendaRegistry
): NavigationState {
  // Fáze 1: Parse URL descriptorů
  const urlLayers = parseStack(searchParams);

  if (urlLayers.length === 0) {
    return recomputeNavigationState([]);
  }

  // Fáze 2: Decode každé vrstvy proti registru.
  // Ready je TOP každého stacku (poslední vrstva dané stackId skupiny; plocha = bez stackId),
  // ne jen globálně poslední — aby se po reloadu načetla vrchní okna všech stacků.
  const topIndexByStack = new Map<string | undefined, number>();
  urlLayers.forEach((l, i) => topIndexByStack.set(l.stackId, i));
  const readyIndices = new Set(topIndexByStack.values());
  const layers = urlLayers.map((urlLayer, index) =>
    restoreLayer(urlLayer, readyIndices.has(index), registry)
  );

  // Fáze 3: V list-detail režimu musí být root vrstva také ready,
  // protože se renderuje společně s detail panelem
  const activatedLayers = activateListDetailRoot(layers, registry);

  return recomputeNavigationState(activatedLayers);
}

/**
 * V list-detail režimu je root vrstva viditelná společně s detail panelem.
 * Pokud je root lazy a jeho první modal je detail-compatible, aktivuj i root.
 */
function activateListDetailRoot(
  layers: LayerDescriptor[],
  registry: AgendaRegistry
): LayerDescriptor[] {
  if (layers.length < 2) return layers;

  const root = layers[0];
  if (!root || root.presentation !== 'root' || root.status !== 'lazy') return layers;

  const config = registry.getById(root.agendaId);
  const layoutMode = config?.presentationHints?.shell?.layoutMode;
  if (layoutMode !== 'list-detail') return layers;

  const allowlist = config?.presentationHints?.shell?.detailAgendaAllowlist ?? [];
  const firstModal = layers[1];
  if (!firstModal || !allowlist.includes(firstModal.agendaId)) return layers;

  const result = layers.slice();
  result[0] = { ...root, status: 'ready' };
  return result;
}

/**
 * Obnoví jednu vrstvu z URL descriptoru.
 * Vrací ready/lazy/error vrstvu podle výsledku dekódování.
 */
function restoreLayer(
  urlLayer: UrlLayerDescriptor,
  isTopLayer: boolean,
  registry: AgendaRegistry
): LayerDescriptor {
  const stackIdPatch = urlLayer.stackId !== undefined ? { stackId: urlLayer.stackId } : {};

  // Hledáme agendu v registru
  const config = registry.getById(urlLayer.agendaId);
  if (!config) {
    return {
      ...createErrorLayerDescriptor({
        agendaId: urlLayer.agendaId,
        presentation: urlLayer.presentation,
        errorReason: {
          code: 'UNKNOWN_AGENDA',
          message: `Agenda "${urlLayer.agendaId}" not found in registry`,
          agendaId: urlLayer.agendaId,
        },
      }),
      ...stackIdPatch,
    };
  }

  // Dekódujeme open parametry
  const openResult = decodeParams(config.open, urlLayer.rawOpenParams, urlLayer.agendaId);
  if (!openResult.success) {
    return {
      ...createErrorLayerDescriptor({
        agendaId: urlLayer.agendaId,
        presentation: urlLayer.presentation,
        errorReason: openResult.error,
      }),
      ...stackIdPatch,
    };
  }

  // Dekódujeme state parametry
  const stateResult = config.state
    ? decodeParams(config.state, urlLayer.rawStateParams, urlLayer.agendaId)
    : { success: true as const, params: {} };
  if (!stateResult.success) {
    return {
      ...createErrorLayerDescriptor({
        agendaId: urlLayer.agendaId,
        presentation: urlLayer.presentation,
        errorReason: stateResult.error,
      }),
      ...stackIdPatch,
    };
  }

  return {
    layerId: generateLayerId(),
    agendaId: urlLayer.agendaId,
    presentation: urlLayer.presentation,
    ...stackIdPatch,
    openParams: openResult.params,
    stateParams: stateResult.params,
    // Vrchní vrstva je ready, podkladové jsou lazy
    status: isTopLayer ? 'ready' : 'lazy',
  };
}

type DecodeResult =
  | { success: true; params: Record<string, unknown> }
  | { success: false; error: NavigationRestoreError };

/**
 * Dekóduje surové string parametry proti codec mapě.
 * Vrací dekódované hodnoty nebo error.
 */
function decodeParams(
  codecMap: ParamCodecMap,
  rawParams: Record<string, string>,
  agendaId: string
): DecodeResult {
  const decoded: Record<string, unknown> = {};

  for (const [name, def] of Object.entries(codecMap)) {
    const rawValue = rawParams[name];

    if (rawValue === undefined) {
      // Povinný parametr chybí
      if (def.required) {
        return {
          success: false,
          error: {
            code: 'DECODE_ERROR',
            message: `Required param "${name}" missing for agenda "${agendaId}"`,
            agendaId,
          },
        };
      }
      // Volitelný parametr – přeskočit
      continue;
    }

    try {
      decoded[name] = def.codec.decode(rawValue);
    } catch {
      return {
        success: false,
        error: {
          code: 'DECODE_ERROR',
          message: `Failed to decode param "${name}" for agenda "${agendaId}" from value "${rawValue}"`,
          agendaId,
        },
      };
    }
  }

  return { success: true, params: decoded };
}
